import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { HexColorPicker } from 'react-colorful';

import { useGetEntityName } from 'hooks-ts/useGetEntity';
import { type BookingCustomStatusDraft, getReadableTextColor } from './types';

interface CustomStatusRowProps {
  customStatus: BookingCustomStatusDraft;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRename: (name: string) => void;
  onRecolor: (color: string) => void;
  onDelete: () => void;
}

const CustomStatusRow = ({
  customStatus,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRename,
  onRecolor,
  onDelete
}: CustomStatusRowProps) => {
  const bookingEntityName = useGetEntityName('booking').toLowerCase();
  const bookingsEntityName = useGetEntityName('bookings').toLowerCase();
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);

  const nameTextColor = getReadableTextColor(customStatus.color);
  const bookingCountLabel = `${customStatus.bookingCount} ${customStatus.bookingCount === 1 ? bookingEntityName : bookingsEntityName}`;

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Stack>
        <IconButton size="small" sx={{ p: 0 }} disabled={!canMoveUp} onClick={onMoveUp} aria-label="Move up">
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={{ p: 0 }} disabled={!canMoveDown} onClick={onMoveDown} aria-label="Move down">
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box
        component="button"
        type="button"
        aria-label="Change colour"
        onClick={(clickEvent) => setColorPickerAnchor(clickEvent.currentTarget)}
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: customStatus.color, border: 1, borderColor: 'divider' }} />
      </Box>
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={() => setColorPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack spacing={1} p={2}>
          <HexColorPicker color={customStatus.color} onChange={onRecolor} />
          <TextField size="small" label="Hex" value={customStatus.color} onChange={(changeEvent) => onRecolor(changeEvent.target.value)} />
        </Stack>
      </Popover>

      <TextField
        size="small"
        fullWidth
        value={customStatus.name}
        onChange={(changeEvent) => onRename(changeEvent.target.value)}
        error={!customStatus.name.trim()}
        slotProps={{ htmlInput: { maxLength: 255, 'aria-label': 'Custom status name' } }}
        sx={{
          '& .MuiOutlinedInput-root': { bgcolor: customStatus.color, color: nameTextColor, fontWeight: 600 }
        }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {bookingCountLabel}
      </Typography>

      <IconButton size="small" onClick={onDelete} aria-label="Delete custom status">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

export default CustomStatusRow;
